import { useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { FormField, IntakeFormShape } from './types';
import { buildFormSchema } from './zodBuilder';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

const BASE_INPUT =
  'w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900';
const ERROR_INPUT =
  'w-full rounded-md border border-red-400 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500';

function FieldRenderer({
  field,
  value,
  onChange,
  error,
}: {
  field: FormField;
  value: unknown;
  onChange: (v: unknown) => void;
  error?: string;
}) {
  const inputClass = error ? ERROR_INPUT : BASE_INPUT;

  return (
    <div className="space-y-1">
      <label htmlFor={`field-${field.id}`} className="block text-sm font-medium text-slate-700">
        {field.label}
        {field.required && (
          <span className="ml-1 text-red-500" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {field.helpText && <p className="text-xs text-slate-500">{field.helpText}</p>}

      {field.type === 'text' && (
        <input
          id={`field-${field.id}`}
          type="text"
          className={inputClass}
          placeholder={field.placeholder}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={!!error}
        />
      )}

      {field.type === 'number' && (
        <input
          id={`field-${field.id}`}
          type="number"
          className={inputClass}
          placeholder={field.placeholder}
          min={field.min}
          max={field.max}
          value={(value as number | '') ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? '' : e.target.valueAsNumber)}
          aria-invalid={!!error}
        />
      )}

      {field.type === 'select' && (
        <select
          id={`field-${field.id}`}
          className={inputClass}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={!!error}
        >
          <option value="">Select…</option>
          {field.options?.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      )}

      {field.type === 'multiselect' && (
        <div id={`field-${field.id}`} className="space-y-1.5">
          {field.options?.map((o) => {
            const checked = ((value as string[]) ?? []).includes(o);
            return (
              <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const cur = (value as string[]) ?? [];
                    onChange(e.target.checked ? [...cur, o] : cur.filter((x) => x !== o));
                  }}
                  className="rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                />
                {o}
              </label>
            );
          })}
        </div>
      )}

      {field.type === 'file' && (
        <input
          id={`field-${field.id}`}
          type="file"
          className={inputClass}
          accept={field.accept}
          onChange={(e) => onChange(e.target.files)}
          aria-invalid={!!error}
        />
      )}

      {error && (
        <p className="text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function IntakeFormPreviewRoute() {
  const { id } = useParams<{ id: string }>();
  const getToken = useSessionTokenGetter();

  const [form, setForm] = useState<IntakeFormShape | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'success' | 'error'>(
    'idle',
  );

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/intake-forms/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const json = await res.json();
        setForm(json.form as IntakeFormShape);
      } catch (e) {
        setFetchError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, getToken]);

  const handleChange = (fieldId: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
    // Clear per-field error on change
    if (errors[fieldId]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;

    // Validate with Zod
    const schema = buildFormSchema(form.fields);
    const result = schema.safeParse(values);
    if (!result.success) {
      const errs: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as string;
        if (key && !errs[key]) errs[key] = issue.message;
      }
      setErrors(errs);
      return;
    }

    setErrors({});
    setSubmitState('submitting');

    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/intake-forms/${id}/submissions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: result.data }),
      });
      setSubmitState(res.ok ? 'success' : 'error');
    } catch {
      setSubmitState('error');
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-sm text-slate-500">Loading…</p>
      </div>
    );
  }

  if (fetchError || !form) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-600">{fetchError ?? 'Form not found.'}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-6">
      {/* Top bar */}
      <div className="flex items-center gap-3">
        <Link
          to={`/forms/${id}/edit`}
          className="text-sm text-slate-500 hover:text-slate-900 hover:underline"
        >
          ← Back to editor
        </Link>
        <span className="rounded-full bg-yellow-50 px-2 py-0.5 text-xs font-medium text-yellow-700">
          Preview mode
        </span>
      </div>

      {/* Form card */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-6 py-5">
          <h1 className="text-xl font-semibold text-slate-900">{form.name}</h1>
          {form.description && <p className="mt-1 text-sm text-slate-500">{form.description}</p>}
        </div>

        {submitState === 'success' ? (
          <div className="px-6 py-8 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-green-600"
                aria-hidden="true"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <p className="font-semibold text-slate-900">Submission recorded.</p>
            <p className="mt-1 text-sm text-slate-500">
              Your response has been saved successfully.
            </p>
            <button
              type="button"
              onClick={() => {
                setValues({});
                setErrors({});
                setSubmitState('idle');
              }}
              className="mt-4 text-sm font-medium text-slate-900 hover:underline"
            >
              Submit another response
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="px-6 py-5 space-y-5">
            {form.fields.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4">
                This form has no fields yet.{' '}
                <Link
                  to={`/forms/${id}/edit`}
                  className="font-medium text-slate-900 hover:underline"
                >
                  Add some in the editor →
                </Link>
              </p>
            )}

            {form.fields.map((field) => (
              <FieldRenderer
                key={field.id}
                field={field}
                value={values[field.id]}
                onChange={(v) => handleChange(field.id, v)}
                error={errors[field.id]}
              />
            ))}

            {submitState === 'error' && (
              <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-sm text-red-600">Submission failed. Please try again.</p>
              </div>
            )}

            {form.fields.length > 0 && (
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submitState === 'submitting'}
                  className="w-full rounded-md bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
                >
                  {submitState === 'submitting' ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
